import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "hiddenField", "display"]
  static values = { url: String, minLength: { type: Number, default: 2 } }

  connect() {
    this.selectedIndex = -1
    document.addEventListener("click", this.handleClickOutside.bind(this))
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside.bind(this))
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  async search() {
    const query = this.inputTarget.value.trim()

    if (query.length < this.minLengthValue) {
      this.hideResults()
      return
    }

    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        const results = await response.json()
        this.displayResults(results)
      }
    } catch (error) {
      console.error("Autocomplete error:", error)
    }
  }

  displayResults(results) {
    if (results.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="px-3 py-2 text-sm text-muted-foreground">
          No products found
        </div>
      `
    } else {
      this.resultsTarget.innerHTML = results.map((result, index) => `
        <button
          type="button"
          class="w-full text-left px-3 py-2 hover:bg-accent focus:bg-accent focus:outline-none"
          data-action="click->autocomplete#select"
          data-autocomplete-id="${result.id}"
          data-autocomplete-name="${result.name}"
          data-autocomplete-sku="${result.sku}"
          data-autocomplete-hpp="${result.hpp}"
          data-autocomplete-selling-price="${result.selling_price}"
          data-autocomplete-price-tiers="${encodeURIComponent(JSON.stringify(result.price_tiers || []))}"
          data-autocomplete-index="${index}"
        >
          <div class="font-medium">${result.name}</div>
          <div class="text-xs text-muted-foreground">${result.sku}</div>
          <div class="text-xs text-muted-foreground">${this.formatPriceTiers(result)}</div>
          ${result.show_hpp ? `<div class="text-xs text-muted-foreground">HPP: ${this.formatNumber(result.hpp)}</div>` : ""}
        </button>
      `).join("")
    }

    this.showResults()
    this.selectedIndex = -1
  }

  formatPriceTiers(result) {
    const tiers = result.price_tiers || []

    if (tiers.length === 0) {
      return `Harga: ${result.selling_price}`
    }

    return tiers.map((tier) => `${tier.label ? `${tier.label} ` : ""}(${tier.min_quantity}+): ${tier.unit_price}`).join(" • ")
  }

  formatNumber(value) {
    return new Intl.NumberFormat("id-ID").format(Number(value) || 0)
  }

  select(event) {
    const button = event.currentTarget
    const id = button.dataset.autocompleteId
    const name = button.dataset.autocompleteName
    const sku = button.dataset.autocompleteSku
    const hpp = button.dataset.autocompleteHpp
    const sellingPrice = button.dataset.autocompleteSellingPrice
    const priceTiers = JSON.parse(decodeURIComponent(button.dataset.autocompletePriceTiers || "%5B%5D"))

    this.hiddenFieldTarget.value = id
    this.inputTarget.value = `${name} (${sku})`

    this.hideResults()

    // Dispatch custom event for any additional handling
    this.element.dispatchEvent(new CustomEvent("autocomplete:selected", {
      bubbles: true,
      detail: { id, name, sku, hpp, sellingPrice, priceTiers }
    }))
  }

  onKeydown(event) {
    const items = this.resultsTarget.querySelectorAll("button")

    if (!this.resultsTarget.classList.contains("hidden") && items.length > 0) {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault()
          this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
          this.highlightItem(items)
          break
        case "ArrowUp":
          event.preventDefault()
          this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
          this.highlightItem(items)
          break
        case "Enter":
          event.preventDefault()
          if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
            items[this.selectedIndex].click()
          }
          break
        case "Escape":
          this.hideResults()
          break
      }
    }
  }

  highlightItem(items) {
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add("bg-accent")
      } else {
        item.classList.remove("bg-accent")
      }
    })
  }

  showResults() {
    this.resultsTarget.classList.remove("hidden")
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
    this.selectedIndex = -1
  }

  clear() {
    this.inputTarget.value = ""
    this.hiddenFieldTarget.value = ""
    this.hideResults()
  }
}
